import { Box, List as ChakraList, List, ListRootProps } from "@biblioteksentralen/react";
import { ResolvedListSummary, ResolvedRecommendationSummary } from "@libry-content/common";
import { ComponentProps } from "react";
import { useTranslation } from "../../utils/hooks/useTranslation";
import RecommendationCard from "../recommendation/RecommendationCard";
import RecommendationCardLarge from "../recommendation/RecommendationCardLarge";
import BookListPreview from "./BookListPreview";
import BookListPreviewLarge from "./BookListPreviewLarge";

type Props = {
  listsAndRecommendations: (ResolvedListSummary | ResolvedRecommendationSummary)[];
  autoFocusIndex?: number;
} & ListRootProps;

/**
 * css'en her skal sørge for at noen boklister dekker hele griden i en egen bredere visning.
 * Løser dette helt med css for at det skal fungere SSR
 * https://bs-redia.atlassian.net/jira/software/projects/CMS/boards/13?selectedIssue=CMS-229
 *  */
const breakPoints = {
  sm: "35em",
  lg: "55em",
};

const StyledList = (props: ComponentProps<typeof ChakraList.Root>) => (
  <ChakraList.Root
    gridTemplateColumns="1fr"
    css={{
      [`@media (min-width: ${breakPoints.sm})`]: {
        gridTemplateColumns: "1fr 1fr",
      },
      [`@media (min-width: ${breakPoints.lg})`]: {
        gridTemplateColumns: "1fr 1fr 1fr",
      },
      listStyle: "none",
    }}
    {...props}
  />
);

const getnthChildStyles = (nthChild: number): any => {
  // TODO: RecursiveCSSSelector<string | CSSWithMultiValues>
  const selector = `:nth-of-type(${nthChild * 2 - 1}n+${nthChild})`;
  return {
    [`&${selector}`]: {
      gridColumn: `span ${nthChild - 1}`,
      "--displaySmall": "none",
      "--displayLarge": "block",
    },
    [`&:not(${selector})`]: {
      gridColumn: "span 1",
      "--displaySmall": "block",
      "--displayLarge": "none",
    },
  };
};

const StyledListItem = (props: ComponentProps<typeof List.Item>) => (
  <List.Item
    css={{
      "--displaySmall": "block",
      "--displayLarge": "none",
      [`@media (min-width: ${breakPoints.sm})`]: getnthChildStyles(3),
      [`@media (min-width: ${breakPoints.lg})`]: getnthChildStyles(4),
    }}
    {...props}
  />
);

export const ListsAndRecommendationsList = ({ listsAndRecommendations, autoFocusIndex, ...props }: Props) => {
  const { t } = useTranslation();

  if (!listsAndRecommendations?.length) return null;

  return (
    <StyledList display="grid" as="ul" gridGap="2.5rem 1rem" {...props}>
      {listsAndRecommendations.map((list, index) =>
        list._type === "list" ? (
          <StyledListItem key={list._id} autoFocus={index === autoFocusIndex}>
            <Box display="var(--displaySmall)">
              <BookListPreview list={list} headingSize="h2" />
            </Box>
            <Box display="var(--displayLarge)">
              <BookListPreviewLarge list={list} borderRadius="lg" tagline={t("Tema")} />
            </Box>
          </StyledListItem>
        ) : (
          <StyledListItem key={list._id} autoFocus={index === autoFocusIndex}>
            <Box display="var(--displaySmall)">
              <RecommendationCard recommendation={list} headingSize="h2" />
            </Box>
            <Box display="var(--displayLarge)">
              <RecommendationCardLarge recommendation={list} />
            </Box>
          </StyledListItem>
        )
      )}
    </StyledList>
  );
};
